import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import * as authService from '../services/auth/authService'
import { getToken, getUserData, clearAllAuthData, hasValidToken } from '../utils/storage'

/**
 * Authentication Store
 * Manages user authentication state using Zustand
 */

const useAuthStore = create(
  persist(
    (set, get) => ({
      // ==================== State ====================
      user: getUserData(),
      token: getToken(),
      isAuthenticated: hasValidToken(),
      isLoading: false,
      error: null,

      // ==================== Actions ====================

      /**
       * Login action
       * @param {Object} credentials - Username and password
       */
      login: async (credentials) => {
        console.log('🟢 [AuthStore] Starting login...')
        set({ isLoading: true, error: null })

        try {
          console.log('🟢 [AuthStore] Calling authService.login...')
          const result = await authService.login(credentials)
          console.log('🟢 [AuthStore] authService.login result:', result)

          set({
            user: result.user,
            token: result.token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })

          console.log('✅ [AuthStore] Login successful, state updated')
          return { success: true, user: result.user }
        } catch (error) {
          console.error('❌ [AuthStore] Login failed:', error)
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.message || 'فشل تسجيل الدخول',
          })

          return { success: false, error: error.message, errors: error.errors }
        }
      },

      /**
       * Logout action
       */
      logout: async () => {
        set({ isLoading: true })

        try {
          await authService.logout()
        } catch (error) {
          console.error('Logout error:', error)
        } finally {
          // Clear state regardless of API result
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })

          // Clear storage
          clearAllAuthData()
        }
      },

      /**
       * Set user data
       * @param {Object} user - User object
       */
      setUser: (user) => {
        set({ user })
      },

      /**
       * Check authentication status
       * Validates token and fetches fresh user data
       */
      checkAuth: async () => {
        const token = getToken()

        // No token means not authenticated
        if (!token || !hasValidToken()) {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          })
          return false
        }

        set({ isLoading: true })

        try {
          const result = await authService.checkAuth()

          if (result.authenticated) {
            set({
              user: result.user,
              token: token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            })
            return true
          } else {
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
            })
            return false
          }
        } catch (error) {
          console.error('Auth check error:', error)

          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          })

          clearAllAuthData()
          return false
        }
      },

      /**
       * Fetch user profile
       */
      fetchProfile: async () => {
        set({ isLoading: true, error: null })

        try {
          const result = await authService.getProfile()

          set({
            user: result.user,
            isLoading: false,
            error: null,
          })

          return { success: true, user: result.user }
        } catch (error) {
          set({
            isLoading: false,
            error: error.message || 'فشل في جلب بيانات المستخدم',
          })

          return { success: false, error: error.message }
        }
      },

      /**
       * Update user profile
       * @param {Object} profileData - Profile data to update
       */
      updateProfile: async (profileData) => {
        set({ isLoading: true, error: null })

        try {
          const result = await authService.updateProfile(profileData)

          set({
            user: result.user,
            isLoading: false,
            error: null,
          })

          return { success: true, user: result.user, message: result.message }
        } catch (error) {
          set({
            isLoading: false,
            error: error.message || 'فشل تحديث الملف الشخصي',
          })

          return { success: false, error: error.message, errors: error.errors }
        }
      },

      /**
       * Change password
       * @param {Object} passwordData - Password change data
       */
      changePassword: async (passwordData) => {
        set({ isLoading: true, error: null })

        try {
          const result = await authService.changePassword(passwordData)

          set({
            isLoading: false,
            error: null,
          })

          return { success: true, message: result.message }
        } catch (error) {
          set({
            isLoading: false,
            error: error.message || 'فشل تغيير كلمة المرور',
          })

          return { success: false, error: error.message, errors: error.errors }
        }
      },

      /**
       * Clear error state
       */
      clearError: () => {
        set({ error: null })
      },

      /**
       * Reset entire auth state
       */
      reset: () => {
        clearAllAuthData()
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        })
      },
    }),
    {
      name: 'delivery-auth-storage', // localStorage key
      partialize: (state) => ({
        // Only persist these fields
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        // Don't persist loading/error states
      }),
    }
  )
)

export default useAuthStore
